import React, { useEffect, useMemo, useState } from 'react';
import { Box, Text, CloseButton, Flex, Link as ChakraLink } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { Notification } from '../../stateManagement/SettingsContext';

interface NotificationBannerProps {
  notifications: Notification[];
  notificationTranslations: Notification[];
  notificationBannerBgColor: string;
  notificationBannerTextColor: string;
}

export const NotificationBanner: React.FC<NotificationBannerProps> = ({
  notifications,
  notificationTranslations = [],
  notificationBannerBgColor,
  notificationBannerTextColor,
}) => {
  const [visibleNotifications, setVisibleNotifications] = useState<Notification[]>([]);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  // Memoize translatedNotifications
  const translatedNotifications = useMemo(() => {
    return notifications.map(notification => {
      const translation = notificationTranslations.find(t => t.id === notification.id);
      return {
        ...notification,
        message: translation?.message ?? notification.message,
      };
    });
  }, [notifications, notificationTranslations]);

  // This will now only run when memoized value changes
  useEffect(() => {
    const now = new Date();
    const valid = translatedNotifications.filter(notif => {
      const start = new Date(notif.startTime);
      const end = new Date(notif.endTime);
      return now >= start && now <= end;
    });
    setVisibleNotifications(valid);
  }, [translatedNotifications]);

  const handleDismiss = (id: string) => {
    setDismissedIds(prev => new Set(prev).add(id));
  };

  const renderMessageWithLinks = (message: string) => {
    const parts = message.split(/(\s+)/).map((part, index) => {
      const key = `${part}-${index}`;

      if (/^https?:\/\/\S+$/i.test(part)) {
        return (
          <ChakraLink
            key={key}
            href={part}
            isExternal
            color={notificationBannerTextColor}
            textDecoration="underline"
          >
            {part}
          </ChakraLink>
        );
      }

      if (/^\/\S+$/i.test(part)) {
        return (
          <ChakraLink
            key={key}
            as={RouterLink}
            to={part}
            color={notificationBannerTextColor}
            textDecoration="underline"
          >
            {part}
          </ChakraLink>
        );
      }

      return (
        <Text as="span" key={key}>
          {part}
        </Text>
      );
    });

    return <Text>{parts}</Text>;
  };

  return (
    <>
      {visibleNotifications
        .filter(notif => !dismissedIds.has(notif.id))
        .map(notif => (
          <Box
            key={notif.id}
            bg={notificationBannerBgColor}
            color={notificationBannerTextColor}
            p={3}
            borderRadius="md"
            mt={4}
          >
            <Flex justify="space-between" align="center">
              {renderMessageWithLinks(notif.message)}
              <CloseButton
                onClick={() => handleDismiss(notif.id)}
                color={notificationBannerTextColor}
              />
            </Flex>
          </Box>
        ))}
    </>
  );
};
